import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Switch, Button, CircularProgress, Chip,
} from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { supabase } from '../../supabaseClient.js';
import useAlert from '../../alert/useAlert.js';
import PayoutSetup from '../Settings/PayoutSetup.js';
import { getReferralChecklist, getMissingProfileFields } from '../../utils/profileCompleteness.js';
import OnboardingLayout from './OnboardingLayout.js';

const TEAL = '#29C0BB';

/**
 * Step 6 — explicit opt-in to case referrals. The share toggle stays disabled
 * until every checklist row passes (verification + profile completion +
 * availability), mirroring the canEnableSharing rule in Update Profile.
 * Completing the step with the toggle Off is always allowed —
 * NGOs and non-referral users just continue to the plan step.
 */
const Step6ClientFeatures = ({ ctx, onContinue, onBack, onStepClick }) => {
  const { userId, initials } = ctx;
  const { setAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [verified, setVerified] = useState(false);
  const [shareProfile, setShareProfile] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadProfile = useCallback(async () => {
    const { data } = await supabase.from('user_accounts').select('*').eq('id', userId).maybeSingle();
    if (!data) return;
    const [nameRes, modeRes, practiceRes, aboutRes, genderRes, phoneRes, addressRes, emailRes, titleRes, orgRes, firstPriceRes, regularPriceRes] = await Promise.all([
      supabase.rpc('decrypt_text', { encrypted_data: data.name }),
      supabase.rpc('decrypt_text', { encrypted_data: data.mode }),
      supabase.rpc('decrypt_text', { encrypted_data: data.practicetype }),
      supabase.rpc('decrypt_text', { encrypted_data: data.about }),
      supabase.rpc('decrypt_text', { encrypted_data: data.gender }),
      supabase.rpc('decrypt_text', { encrypted_data: data.phone_number }),
      supabase.rpc('decrypt_text', { encrypted_data: data.address }),
      supabase.rpc('decrypt_text', { encrypted_data: data.secondary_email }),
      supabase.rpc('decrypt_text', { encrypted_data: data.practice_title }),
      supabase.rpc('decrypt_text', { encrypted_data: data.organisation }),
      supabase.rpc('decrypt_text', { encrypted_data: data.first_session_price }),
      supabase.rpc('decrypt_text', { encrypted_data: data.regular_session_price }),
    ]);
    setProfile({
      name: nameRes.data || '',
      mode: modeRes.data || '',
      practiceType: practiceRes.data || '',
      about: aboutRes.data || '',
      gender: genderRes.data || '',
      phoneNumber: phoneRes.data || '',
      address: addressRes.data || '',
      secondaryEmail: emailRes.data || '',
      practiceTitle: titleRes.data || '',
      organisation: orgRes.data || '',
      professionalQualification: data.professional_qualification || '',
      experienceYears: data.experience || '',
      languages: data.languages || [],
      interventionApproach: data.intervention_approach || [],
      assessmentTypes: data.assessment_types || [],
      preferredCaseTypes: data.preferred_case_types || [],
      clientGroups: data.client_groups || [],
      serviceFormats: data.service_formats || [],
      firstSessionDuration: data.first_session_duration || '',
      firstSessionPrice: firstPriceRes.data || '',
      regularSessionDuration: data.regular_session_duration || '',
      regularSessionPrice: regularPriceRes.data || '',
      clinicLocations: data.clinic_locations || [],
    });
    setVerified(Boolean(data.registered_number_verified));
    setShareProfile(Boolean(data.registered_number_verified) && Boolean(data.share_profile));
    setLoading(false);
  }, [userId]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const payoutComplete = payoutStatus === 'complete';
  const checklist = profile ? getReferralChecklist(profile, { verified }) : [];
  const allComplete = checklist.length > 0 && checklist.every((item) => item.complete);
  const missingFields = profile ? getMissingProfileFields(profile) : [];

  const handleToggle = async (event) => {
    const next = event.target.checked;
    if (next && !allComplete) return;
    setShareProfile(next);
    const { error } = await supabase
      .from('user_accounts')
      .update({ share_profile: next && allComplete })
      .eq('id', userId);
    if (error) {
      setAlert('Unable to update sharing preference.', 'error');
      setShareProfile(!next);
    }
  };

  const handleContinue = async () => {
    setSaving(true);
    // Toggle state already persisted on change; nothing else to save here.
    setSaving(false);
    onContinue();
  };

  const rowIcon = (item) => {
    if (item.complete) return <CheckCircleIcon sx={{ color: '#2e9e4f', fontSize: 20 }} />;
    return <RadioButtonUncheckedIcon sx={{ color: '#8a94a0', fontSize: 20 }} />;
  };

  const rowStatus = (item) => {
    if (item.complete) {
      return <Typography variant="body2" sx={{ color: '#2e9e4f', fontWeight: 600 }}>{item.key === 'certification' ? 'Verified' : 'Completed'}</Typography>;
    }
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="body2" sx={{ color: '#e8a13c', fontWeight: 600 }}>Pending</Typography>
        <ChevronRightIcon sx={{ fontSize: 18, color: '#e8a13c' }} />
      </Stack>
    );
  };

  if (loading) {
    return (
      <OnboardingLayout step={6} title="Choose your client-facing features" avatarInitials={initials}>
        <CircularProgress sx={{ color: TEAL }} />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      step={6}
      title="Choose your client-facing features"
      description="Decide whether you'd like to share your profile to receive case referrals from our AI matching system. This feature is optional. You can finish setup without enabling it."
      sidebarExtra={
        <Stack spacing={2}>
          {[
            { icon: <VerifiedUserOutlinedIcon sx={{ color: TEAL, fontSize: 20 }} />, title: "You're in control", text: 'Choose when and how you want to be visible to clients.' },
            { icon: <EventAvailableOutlinedIcon sx={{ color: TEAL, fontSize: 20 }} />, title: 'Enable when ready', text: "Turn on this feature anytime once your profile is complete and you're ready." },
            { icon: <LockOutlinedIcon sx={{ color: TEAL, fontSize: 20 }} />, title: 'Secure & private', text: 'Your information is private until you choose to share it.' },
          ].map((item) => (
            <Stack key={item.title} direction="row" spacing={1}>
              <Box sx={{ mt: '2px' }}>{item.icon}</Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                <Typography variant="body2" sx={{ color: '#5b6770' }}>{item.text}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      }
      avatarInitials={initials}
      onStepClick={onStepClick}
      onBack={onBack}
      onContinue={handleContinue}
      continueLabel="Continue"
      saving={saving}
    >
      {/* Share toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <PublicOutlinedIcon sx={{ color: TEAL, fontSize: 28, mt: '2px' }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Share my profile for case referral</Typography>
            <Typography variant="body2" sx={{ color: '#5b6770' }}>
              Allow AI to match cases to you based on your specialty and availability.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            checked={shareProfile}
            onChange={handleToggle}
            disabled={!allComplete && !shareProfile}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: TEAL },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: TEAL },
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#5b6770' }}>
            {shareProfile ? 'On' : 'Off'}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#eef6fb', borderRadius: 2, display: 'flex', gap: 1 }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: '#3b82c4', mt: '2px' }} />
        <Typography variant="body2" sx={{ color: '#39566d' }}>
          Your profile will remain private and not visible for case referrals until you turn this on.
        </Typography>
      </Box>

      {/* Checklist */}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mt: 3 }}>
        <Box sx={{ flex: 2, border: '1px solid #e6eaee', borderRadius: 3, p: 3 }}>
          <Typography sx={{ fontWeight: 700 }}>
            To enable case referrals, make sure you've completed everything below
          </Typography>
          <Typography variant="body2" sx={{ color: '#5b6770', mb: 2 }}>
            All items must be complete before you can share your profile.
          </Typography>
          <Stack divider={<Box sx={{ borderBottom: '1px solid #eef1f4' }} />}>
            {checklist.map((item) => (
              <Stack key={item.key} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {rowIcon(item)}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.label}</Typography>
                </Stack>
                {rowStatus(item)}
              </Stack>
            ))}
          </Stack>
          {missingFields.length > 0 && (
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fdf7ec', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ color: '#8a6b2f' }}>
                Still missing: {missingFields.join(', ')}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, backgroundColor: '#f6f8f9', borderRadius: 3, p: 3, alignSelf: 'flex-start' }}>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Why these are needed</Typography>
          {[
            { icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 18, color: TEAL }} />, title: 'Better matches', text: 'Complete information helps our AI match the right cases to you.' },
            { icon: <LockOutlinedIcon sx={{ fontSize: 18, color: TEAL }} />, title: 'Trust & safety', text: 'Verified professionals give clients and referral partners confidence.' },
            { icon: <GroupsOutlinedIcon sx={{ fontSize: 18, color: TEAL }} />, title: 'Better outcomes', text: 'Up-to-date profiles lead to better matches and successful outcomes.' },
          ].map((item) => (
            <Stack key={item.title} direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <Box sx={{ mt: '2px' }}>{item.icon}</Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                <Typography variant="body2" sx={{ color: '#5b6770' }}>{item.text}</Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      </Stack>

      {/* Payout gateway */}
      {!payoutComplete && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#fdf7ec', borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <ErrorOutlineIcon sx={{ color: '#e8a13c', fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#a8742a' }}>Payout setup for payments</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: '#8a6b2f' }}>
            A payout account is only required before receiving payments through TikCare; it is not required to enable referral sharing.
          </Typography>
        </Box>
      )}
      <PayoutSetup userid={userId} onStatusChange={setPayoutStatus} />

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
        <EventAvailableOutlinedIcon sx={{ color: TEAL }} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Not ready to enable case referrals yet?</Typography>
          <Typography variant="body2" sx={{ color: '#5b6770' }}>
            No problem. You can finish setup now and enable this feature later from your dashboard.
          </Typography>
        </Box>
      </Stack>
    </OnboardingLayout>
  );
};

export default Step6ClientFeatures;
